import { backoffice, BackofficeUser } from "@/endpoints/dryp/backoffice";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { DialogHeader } from "@/components/ui/dialog";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { useStore } from "@/hooks/useStore";
import { observer } from "mobx-react-lite";
import { FC, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AssignedOrganization } from "./AssignedOrganization";
import { AddOrganization } from "./AddOrganization";

interface AssignOrganizationsProps {
	user: BackofficeUser;
	onSuccess: () => void;
}

export const AssignOrganizations: FC<AssignOrganizationsProps> = observer(({ user: targetUser, onSuccess }) => {
	const { userStore } = useStore();
	const isOwnUser = userStore.getUser?.name === targetUser.userName;

	const allOrganizationsApi = useAsyncStateStore(backoffice.getOrganizations);
	const userOrganizationsApi = useAsyncStateStore(backoffice.getUserOrganizations);
	const addOrganizationApi = useAsyncStateStore(backoffice.addUserOrganizations);
	const updateOrganizationApi = useAsyncStateStore(backoffice.updateUserOrganizations);
	const removeOrganizationApi = useAsyncStateStore(backoffice.removeUserOrganizations);

	useEffect(() => {
		if (isOwnUser) return;
		allOrganizationsApi.fetch({ pageNumber: 0, pageSize: 100000 }, { sort: "name", direction: "asc" });
		userOrganizationsApi.fetch(targetUser.id);
		return () => onSuccess();
	}, []);

	const loading = allOrganizationsApi.loading || userOrganizationsApi.loading;
	const allOrganizations = allOrganizationsApi.data?.items || [];
	const assignments = userOrganizationsApi.data || [];
	const assignedOrganizationIds = new Set(assignments.map(assignment => assignment.organizationId));
	const availableOrganizations = allOrganizations.filter(
		organization => !assignedOrganizationIds.has(organization.id),
	);

	const refresh = () => {
		userOrganizationsApi.fetch(targetUser.id);
		onSuccess();
	};

	const handleAdd = async (organizationId: string, role: string) => {
		try {
			await addOrganizationApi.fetch(targetUser.id, { [organizationId]: role });
			refresh();
			toast.success("Organization added");
		} catch {
			toast.error("Failed to add organization");
		}
	};

	const handleRemove = async (organizationId: string) => {
		try {
			await removeOrganizationApi.fetch(targetUser.id, [organizationId]);
			refresh();
			toast.success("Organization removed");
		} catch {
			toast.error("Failed to remove organization");
		}
	};

	const handleRoleChange = async (organizationId: string, roleName: string) => {
		try {
			await updateOrganizationApi.fetch(targetUser.id, { [organizationId]: roleName });
			refresh();
			toast.success("Role updated");
		} catch {
			toast.error("Failed to update role");
		}
	};

	const error =
		allOrganizationsApi.error ||
		userOrganizationsApi.error ||
		addOrganizationApi.error ||
		updateOrganizationApi.error ||
		removeOrganizationApi.error;

	return (
		<>
			<DialogHeader>
				<DialogTitle>Manage Organizations - {targetUser.userName}</DialogTitle>
				<DialogDescription className="sr-only">
					Manage organization assignments for {targetUser.userName}.
				</DialogDescription>
			</DialogHeader>

			{isOwnUser ? (
				<p className="text-sm text-muted-foreground py-4">
					You cannot modify your own organization assignments. Please contact customer services for
					assistance.
				</p>
			) : loading ? (
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{error && <p className="text-sm text-destructive">Something went wrong.</p>}

					<div className="flex flex-col gap-2">
						{assignments.map(assignment => (
							<AssignedOrganization
								key={assignment.organizationId}
								assignment={assignment}
								onRoleChange={handleRoleChange}
								onRemove={handleRemove}
								updateLoading={updateOrganizationApi.loading}
								removeLoading={removeOrganizationApi.loading}
							/>
						))}

						{assignments.length === 0 && (
							<p className="text-sm text-muted-foreground py-2">No organizations assigned.</p>
						)}
					</div>

					<AddOrganization
						availableOrganizations={availableOrganizations}
						onAdd={handleAdd}
						loading={addOrganizationApi.loading}
					/>
				</div>
			)}
		</>
	);
});
